import { useState } from 'react'
import { Activity, AlertCircle } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { cn } from '@/lib/utils'

type Mode = 'login' | 'register'

export default function Login() {
  const { login, register } = useAuth()
  const [mode, setMode] = useState<Mode>('login')
  const [email, setEmail] = useState('')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsLoading(true)

    const result =
      mode === 'login'
        ? await login(email, password)
        : await register(email, username, password)

    setIsLoading(false)

    if (!result.success) {
      setError(result.error ?? 'Something went wrong')
    }
  }

  return (
    <div className="min-h-screen bg-[#141517] flex">
      {/* Left: Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#1a1b1e] border-r border-white/[0.06] flex-col justify-between p-12">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-accent rounded-xl flex items-center justify-center">
            <Activity className="w-5 h-5 text-black" strokeWidth={2.5} />
          </div>
          <span className="text-lg font-semibold">StockSim</span>
        </div>

        <div className="space-y-6">
          <div className="space-y-2">
            <h1 className="text-4xl font-bold leading-tight">
              Practice trading.<br />
              <span className="text-accent">Risk nothing.</span>
            </h1>
            <p className="text-white/40 text-lg">
              Start with $100,000 virtual cash and learn to invest with real market data.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {[
              { label: 'Starting Balance', value: '$100,000' },
              { label: 'Real-time Quotes', value: 'Live Data' },
              { label: 'Commission', value: 'Free' },
              { label: 'Risk', value: 'Zero' },
            ].map(({ label, value }) => (
              <div key={label} className="bg-white/[0.04] rounded-xl p-4">
                <div className="text-xs text-white/30 mb-1">{label}</div>
                <div className="font-semibold text-accent">{value}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="text-xs text-white/20">
          Uses Finnhub API for real-time market data
        </div>
      </div>

      {/* Right: Auth form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-sm space-y-8">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-2">
            <div className="w-8 h-8 bg-accent rounded-lg flex items-center justify-center">
              <Activity className="w-4 h-4 text-black" strokeWidth={2.5} />
            </div>
            <span className="font-semibold">StockSim</span>
          </div>

          <div>
            <h2 className="text-2xl font-bold">
              {mode === 'login' ? 'Welcome back' : 'Create account'}
            </h2>
            <p className="text-white/40 text-sm mt-1">
              {mode === 'login'
                ? 'Sign in to your account'
                : 'Start with $100,000 virtual cash'}
            </p>
          </div>

          {/* Mode toggle */}
          <div className="flex bg-white/[0.06] rounded-xl p-1">
            {(['login', 'register'] as Mode[]).map((m) => (
              <button
                key={m}
                onClick={() => { setMode(m); setError(null) }}
                className={cn(
                  'flex-1 py-2 rounded-lg text-sm font-medium transition-all',
                  mode === m
                    ? 'bg-white/[0.1] text-white'
                    : 'text-white/40 hover:text-white/70'
                )}
              >
                {m === 'login' ? 'Sign In' : 'Register'}
              </button>
            ))}
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs text-white/40 uppercase tracking-wider mb-1.5">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@example.com"
                className="input-base w-full"
              />
            </div>

            {mode === 'register' && (
              <div>
                <label className="block text-xs text-white/40 uppercase tracking-wider mb-1.5">
                  Username
                </label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  minLength={3}
                  maxLength={20}
                  pattern="[a-zA-Z0-9_]+"
                  placeholder="trader_joe"
                  className="input-base w-full"
                />
              </div>
            )}

            <div>
              <label className="block text-xs text-white/40 uppercase tracking-wider mb-1.5">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                placeholder="••••••••"
                className="input-base w-full"
              />
            </div>

            {error && (
              <div className="flex items-start gap-2 text-loss text-sm bg-loss-muted rounded-lg px-3 py-2.5">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                {error}
              </div>
            )}

            <button type="submit" disabled={isLoading} className="btn-primary w-full py-3 text-sm">
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                  {mode === 'login' ? 'Signing in...' : 'Creating account...'}
                </span>
              ) : mode === 'login' ? (
                'Sign In'
              ) : (
                'Create Account'
              )}
            </button>
          </form>

          {mode === 'register' && (
            <p className="text-xs text-white/20 text-center">
              Password must be at least 8 characters.
              Username: 3–20 chars, letters, numbers, underscores.
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
